import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Lock, ArrowRight, ShieldCheck, AlertTriangle } from "lucide-react";
import { AuthCard, PasswordField, AuthButton } from "@/components/auth/auth-form";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) setError("This reset link is missing a token.");
  }, [token]);

  const valid = password.length >= 8 && password === confirm;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid || !token) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/api/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || "This link is invalid or expired.");
      } else {
        setSuccess(true);
        setTimeout(() => navigate("/login?reset=true"), 1500);
      }
    } catch {
      setError("Network error — please try again.");
    }
    setLoading(false);
  };

  if (!token) {
    return (
      <AuthCard
        eyebrow="Reset password"
        title="Link looks broken"
        subtitle="The reset link you used is missing a token. Request a new one to continue."
        footer={
          <Link
            to="/forgot-password"
            className="flex items-center justify-center gap-1.5 text-sm font-semibold text-primary hover:underline"
          >
            Request a new link <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        }
      >
        <div className="flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3.5 py-3">
          <AlertTriangle className="h-4 w-4 text-amber-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-200/90 leading-relaxed">
            For your security, reset links are single-use and expire after one hour. If
            you copy-pasted from your email and the URL got cut off, paste the full link
            and try again.
          </p>
        </div>
      </AuthCard>
    );
  }

  if (success) {
    return (
      <AuthCard
        eyebrow="All done"
        title="Password updated"
        subtitle="You'll be redirected to sign in. Use your new password from now on."
      >
        <div className="flex flex-col items-center py-4">
          <div className="relative mb-5">
            <div className="absolute inset-0 rounded-full bg-emerald-500/20 blur-xl" />
            <div className="relative flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-500/10 ring-1 ring-emerald-500/30">
              <ShieldCheck className="h-8 w-8 text-emerald-400" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            For your safety, all other sessions have been signed out.
          </p>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      eyebrow="New password"
      title="Pick a strong password"
      subtitle="Use at least 8 characters. Mix letters, numbers, and symbols for the best score."
      footer={
        <p className="text-center text-xs text-muted-foreground">
          Need a fresh link?{" "}
          <Link to="/forgot-password" className="text-primary font-semibold hover:underline">
            Restart reset
          </Link>
        </p>
      }
    >
      <form onSubmit={submit} className="space-y-4">
        <PasswordField
          label="New password"
          autoComplete="new-password"
          required
          icon={<Lock className="h-4 w-4" />}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="At least 8 characters"
          showStrength
        />

        <PasswordField
          label="Confirm new password"
          autoComplete="new-password"
          required
          icon={<Lock className="h-4 w-4" />}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder="Repeat your password"
          matchValue={password}
        />

        {error && (
          <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-3.5 py-2.5 text-xs text-rose-300">
            {error}
          </div>
        )}

        <AuthButton type="submit" loading={loading} disabled={!valid}>
          Update password <ArrowRight className="h-4 w-4" />
        </AuthButton>

        <div className="rounded-xl border border-border/40 bg-background/30 px-3 py-2.5 text-[11px] text-muted-foreground leading-relaxed">
          <span className="font-bold text-foreground/80">Heads up:</span> updating your
          password will sign out any other devices currently logged in.
        </div>
      </form>
    </AuthCard>
  );
}
